/**
 * ChallengePlugin - Goal-directed maze/puzzle activity type.
 *
 * Ported from bumblebee experiment. Renders goal markers, obstacles,
 * validates sprite position against goal, tracks boundary collisions.
 *
 * @see GitHub Issue #852
 */

import GoalMarker from './GoalMarker';
import ObstacleRenderer from './ObstacleRenderer';
import BoundaryGuard from './BoundaryGuard';
import StoryWalkFlow from './StoryWalkFlow';
import BugBandFlow from './BugBandFlow';
import MoonwalkMachineFlow from './MoonwalkMachineFlow';
import TreasureMapFlow from './TreasureMapFlow';

export default class ChallengePlugin {
    constructor () {
        this._activityData = null;
        this._stateMachine = null;
        this._container = null;
        this._goalMarker = null;
        this._obstacleRenderer = null;
        this._boundaryGuard = null;
        this._positionPollInterval = null;
        this._storyWalk = null;
        this._bugBand = null;
        this._moonwalk = null;
        this._treasureMap = null;
    }

    /**
     * Initialize for a specific challenge activity.
     */
    init (activityData, stateMachine, container) {
        this._activityData = activityData;
        this._stateMachine = stateMachine;
        this._container = container;

        if (activityData.treasureMap) {
            this._treasureMap = new TreasureMapFlow(activityData, stateMachine, container);
            return this._treasureMap.init();
        }

        if (activityData.moonwalkMachine) {
            this._moonwalk = new MoonwalkMachineFlow(activityData, stateMachine, container);
            return this._moonwalk.init();
        }

        if (activityData.storyWalk) {
            this._storyWalk = new StoryWalkFlow(activityData, stateMachine, container);
            return this._storyWalk.init();
        }

        if (activityData.bugBand) {
            this._bugBand = new BugBandFlow(activityData, stateMachine, container);
            return this._bugBand.init();
        }

        const criteria = activityData.successCriteria;
        const stage = container.querySelector('.stage') || container;

        // Render goal marker
        if (criteria.goalX !== undefined && criteria.goalY !== undefined) {
            this._goalMarker = new GoalMarker(
                stage,
                criteria.goalX,
                criteria.goalY,
                criteria.tolerance || 30
            );
            this._goalMarker.show();
        }

        // Render obstacles
        if (activityData.obstacles && activityData.obstacles.length > 0) {
            this._obstacleRenderer = new ObstacleRenderer(stage, activityData.obstacles);
        }

        // Set up boundary guard
        this._boundaryGuard = new BoundaryGuard(
            480, 360, // ScratchJr stage dimensions
            activityData.obstacles || [],
            {
                onBoundary: (spriteId) => {
                    this._showBoundaryToast(spriteId);
                }
            }
        );
    }

    /**
     * React to state machine transitions.
     */
    onStateChange (prevState, newState) {
        if (this._treasureMap) {
            this._treasureMap.onStateChange(prevState, newState);
            return;
        }

        if (this._moonwalk) {
            this._moonwalk.onStateChange(prevState, newState);
            return;
        }

        if (this._storyWalk) {
            this._storyWalk.onStateChange(prevState, newState);
            return;
        }

        if (this._bugBand) {
            this._bugBand.onStateChange(prevState, newState);
            return;
        }

        switch (newState) {
        case 'RUNNING':
            this._startPositionPolling();
            break;
        case 'VALIDATING':
            this._stopPositionPolling();
            this._validate();
            break;
        case 'BUILDING':
        case 'READY':
            this._stopPositionPolling();
            break;
        }
    }

    /**
     * Evaluate sprite position against goal.
     * @returns {object}
     */
    evaluate () {
        if (this._treasureMap) {
            return this._treasureMap.evaluate();
        }

        if (this._moonwalk) {
            return this._moonwalk.evaluate();
        }

        if (this._storyWalk) {
            return this._storyWalk.evaluate();
        }

        if (this._bugBand) {
            return this._bugBand.evaluate();
        }

        const criteria = this._activityData.successCriteria;
        const sprite = this._getPrimarySprite();
        if (!sprite) return {success: false, usedBlocks: 0};

        const dx = sprite.x - criteria.goalX;
        const dy = sprite.y - criteria.goalY;
        const distance = Math.sqrt(dx * dx + dy * dy);
        const tolerance = criteria.tolerance || 30;
        const overlap = this._spriteOverlapPercent(
            sprite.x, sprite.y, sprite.w, sprite.h,
            criteria.goalX, criteria.goalY, tolerance
        );

        return {
            success: overlap >= 0.8,
            distance: distance,
            overlap: overlap,
            usedBlocks: this.getBlockCount(),
            optimalBlocks: criteria.optimalBlocks || 1
        };
    }

    /**
     * Get current block count.
     * @returns {number}
     */
    getBlockCount () {
        if (this._treasureMap) {
            return this._treasureMap.getBlockCount();
        }

        if (this._moonwalk) {
            return this._moonwalk.getBlockCount();
        }

        if (this._storyWalk) {
            return this._storyWalk.getBlockCount();
        }

        if (this._bugBand) {
            return this._bugBand.getBlockCount();
        }

        const blocks = this._container.querySelectorAll('.scripts .blockgroup');
        return blocks ? blocks.length : 0;
    }

    /**
     * Show a hint overlay.
     */
    showHint (hint) {
        if (this._treasureMap) {
            this._treasureMap.showHint(hint);
            return;
        }

        if (this._moonwalk) {
            this._moonwalk.showHint(hint);
            return;
        }

        if (this._storyWalk) {
            this._storyWalk.showHint(hint);
            return;
        }

        if (this._bugBand) {
            this._bugBand.showHint(hint);
            return;
        }

        if (hint.pathOverlay && hint.pathOverlay.waypoints) {
            this._renderPathOverlay(hint.pathOverlay);
        }
    }

    showWalkthrough () {
        // Show all hints sequentially
    }

    captureRunStartPosition () {
        if (this._treasureMap && typeof this._treasureMap.captureRunStartPosition === 'function') {
            this._treasureMap.captureRunStartPosition();
        }
        if (this._moonwalk && typeof this._moonwalk.captureRunStartPosition === 'function') {
            this._moonwalk.captureRunStartPosition();
        }
        if (this._storyWalk && typeof this._storyWalk.captureRunStartPosition === 'function') {
            this._storyWalk.captureRunStartPosition();
        }
        if (this._bugBand && typeof this._bugBand.captureRunStartPosition === 'function') {
            this._bugBand.captureRunStartPosition();
        }
    }

    shouldResetSpritesBeforeRun () {
        if (this._treasureMap && typeof this._treasureMap.shouldResetSpritesBeforeRun === 'function') {
            return this._treasureMap.shouldResetSpritesBeforeRun();
        }
        if (this._moonwalk && typeof this._moonwalk.shouldResetSpritesBeforeRun === 'function') {
            return this._moonwalk.shouldResetSpritesBeforeRun();
        }
        if (this._storyWalk && typeof this._storyWalk.shouldResetSpritesBeforeRun === 'function') {
            return this._storyWalk.shouldResetSpritesBeforeRun();
        }
        return true;
    }

    shouldEvaluateWhileThreadsRunning (runtime) {
        if (this._treasureMap && typeof this._treasureMap.shouldEvaluateWhileThreadsRunning === 'function') {
            return this._treasureMap.shouldEvaluateWhileThreadsRunning(runtime);
        }
        if (this._moonwalk && typeof this._moonwalk.shouldEvaluateWhileThreadsRunning === 'function') {
            return this._moonwalk.shouldEvaluateWhileThreadsRunning(runtime);
        }
        if (this._storyWalk && typeof this._storyWalk.shouldEvaluateWhileThreadsRunning === 'function') {
            return this._storyWalk.shouldEvaluateWhileThreadsRunning(runtime);
        }
        if (this._bugBand && typeof this._bugBand.shouldEvaluateWhileThreadsRunning === 'function') {
            return this._bugBand.shouldEvaluateWhileThreadsRunning(runtime);
        }
        return false;
    }

    restartActivity () {
        if (this._treasureMap && typeof this._treasureMap.restartActivity === 'function') {
            return this._treasureMap.restartActivity();
        }

        if (this._moonwalk && typeof this._moonwalk.restartActivity === 'function') {
            return this._moonwalk.restartActivity();
        }

        if (this._storyWalk && typeof this._storyWalk.restartActivity === 'function') {
            return this._storyWalk.restartActivity();
        }

        if (this._bugBand && typeof this._bugBand.restartActivity === 'function') {
            return this._bugBand.restartActivity();
        }

        if (this._stateMachine) {
            this._stateMachine.transition('BUILDING');
        }
        return false;
    }

    getActiveFlow (kind) {
        if (kind === 'treasureMap') {
            return this._treasureMap || null;
        }
        if (kind === 'moonwalk') {
            return this._moonwalk || null;
        }
        if (kind === 'storyWalk') {
            return this._storyWalk || null;
        }
        if (kind === 'bugBand') {
            return this._bugBand || null;
        }
        return this._treasureMap || this._moonwalk || this._bugBand || this._storyWalk || null;
    }

    isMultiSpriteActivity () {
        return Boolean(this._bugBand) || Boolean(this._treasureMap);
    }

    destroy () {
        if (this._treasureMap) {
            this._treasureMap.destroy();
            this._treasureMap = null;
            return;
        }

        if (this._moonwalk) {
            this._moonwalk.destroy();
            this._moonwalk = null;
            return;
        }

        if (this._storyWalk) {
            this._storyWalk.destroy();
            this._storyWalk = null;
            return;
        }

        if (this._bugBand) {
            this._bugBand.destroy();
            this._bugBand = null;
            return;
        }

        this._stopPositionPolling();
        if (this._goalMarker) this._goalMarker.destroy();
        if (this._obstacleRenderer) this._obstacleRenderer.destroy();
        if (this._boundaryGuard) this._boundaryGuard.destroy();
    }

    // --- Private ---

    _startPositionPolling () {
        this._positionPollInterval = setInterval(() => {
            const sprite = this._getPrimarySprite();
            if (sprite && this._boundaryGuard) {
                const clamped = this._boundaryGuard.checkAndClamp(sprite);
                // Apply clamped position back to the sprite element
                if (sprite.element && (clamped.x !== sprite.x || clamped.y !== sprite.y)) {
                    sprite.element.style.left = `${clamped.x}px`;
                    sprite.element.style.top = `${clamped.y}px`;
                    sprite.element.style.transform = `translate(${clamped.x}px, ${clamped.y}px)`;
                }
            }
        }, 100);
    }

    _stopPositionPolling () {
        if (this._positionPollInterval) {
            clearInterval(this._positionPollInterval);
            this._positionPollInterval = null;
        }
    }

    _validate () {
        const result = this.evaluate();
        if (result.success) {
            this._stateMachine.transition('SUCCESS');
        } else {
            this._stateMachine.transition('FAILED');
        }
    }

    _getPrimarySprite () {
        // ScratchJr stores sprite data — get from the stage
        const spriteEl = this._container.querySelector('.stage .sprite');
        if (!spriteEl) return null;

        const transform = spriteEl.style.transform || '';
        const match = transform.match(/translate\(([^,]+)px,\s*([^)]+)px\)/);
        const w = parseFloat(spriteEl.style.width) || spriteEl.offsetWidth || 48;
        const h = parseFloat(spriteEl.style.height) || spriteEl.offsetHeight || 48;
        if (match) {
            return {
                x: parseFloat(match[1]),
                y: parseFloat(match[2]),
                w: w,
                h: h,
                element: spriteEl
            };
        }

        return {
            x: parseFloat(spriteEl.style.left) || 0,
            y: parseFloat(spriteEl.style.top) || 0,
            w: w,
            h: h,
            element: spriteEl
        };
    }

    _spriteOverlapPercent (spriteX, spriteY, spriteW, spriteH, goalX, goalY, goalRadius) {
        const gridSize = 10;
        let inside = 0;
        const total = gridSize * gridSize;
        const r2 = goalRadius * goalRadius;
        for (let row = 0; row < gridSize; row++) {
            const py = spriteY + (spriteH * (row + 0.5)) / gridSize;
            for (let col = 0; col < gridSize; col++) {
                const px = spriteX + (spriteW * (col + 0.5)) / gridSize;
                const dx = px - goalX;
                const dy = py - goalY;
                if (dx * dx + dy * dy <= r2) {
                    inside++;
                }
            }
        }
        return inside / total;
    }

    _showBoundaryToast () {
        const toast = document.createElement('div');
        toast.className = 'activity-boundary-toast';
        toast.textContent = '\u26A0'; // Warning sign
        this._container.appendChild(toast);
        setTimeout(() => {
            if (toast.parentNode) toast.parentNode.removeChild(toast);
        }, 1500);
    }

    _renderPathOverlay (pathOverlay) {
        // Render SVG path overlay on stage
        const stage = this._container.querySelector('.stage') || this._container;
        const overlay = document.createElement('div');
        overlay.className = 'activity-path-overlay';

        if (pathOverlay.waypoints) {
            const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
            svg.setAttribute('viewBox', '0 0 480 360');
            svg.style.position = 'absolute';
            svg.style.top = '0';
            svg.style.left = '0';
            svg.style.width = '100%';
            svg.style.height = '100%';
            svg.style.pointerEvents = 'none';

            const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
            const d = pathOverlay.waypoints.map((wp, i) =>
                `${i === 0 ? 'M' : 'L'} ${wp.x} ${wp.y}`
            ).join(' ');
            path.setAttribute('d', d);
            path.setAttribute('stroke', '#4CAF50');
            path.setAttribute('stroke-width', '3');
            path.setAttribute('stroke-dasharray', '8,4');
            path.setAttribute('fill', 'none');
            svg.appendChild(path);
            overlay.appendChild(svg);
        }

        overlay.addEventListener('pointerup', () => {
            if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
        });
        stage.appendChild(overlay);
    }
}
